import { useQuery } from "@apollo/client"
import { Link, useNavigate, useParams } from "react-router-dom"
import { gql } from "~/__generated__"
import { useDocumentTitle } from "~/common/use-document-title"
import { useSafeMutation } from "~/common/use-safe-mutation"
import { myLettermemeManagePath, settingsPath } from "~/common/paths"
import { GraphqlError } from "~/ui/errors"
import { useToast } from "~/ui/use-toast"
import { ScreenLoading } from "~/ui/screen-loading"
import { LettermemeForm, LettermemeFormValues } from "./lettermeme-form"

const lettermemeQuery = gql(/* GraphQL */ `
  query EditLettermeme($id: ID!) {
    lettermeme(id: $id) {
      id
      name
      urlPath
      shareableEmailIn
      secretEmailIn
      publicDescription
      emailHeaderText
      summaryTonePrompt
      cartoonStylePrompt
      emailCadence
      updateCadence
      privacy
      generateCartoons
      lettermemeUsers {
        owner
        user {
          email
        }
      }
    }
  }
`)

const updateLettermemeMutation = gql(/* GraphQL */ `
  mutation UpdateLettermeme($input: LettermemeUpdateInput!) {
    lettermemeUpdate(input: $input) {
      lettermeme {
        id
        name
        urlPath
        shareableEmailIn
        publicDescription
        emailHeaderText
        summaryTonePrompt
        cartoonStylePrompt
        emailCadence
        updateCadence
        privacy
        generateCartoons
      }
    }
  }
`)

export const EditLettermemeScreen: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const { toast } = useToast()
  const navigate = useNavigate()

  const { data, error, loading } = useQuery(lettermemeQuery, {
    variables: { id: id! },
  })
  const lettermeme = data?.lettermeme

  useDocumentTitle(data?.lettermeme ? `Edit ${data.lettermeme.name}` : "Edit Lettermeme")

  const [updateLettermeme, mutationResult] = useSafeMutation(updateLettermemeMutation, {
    onCompleted: () => {
      toast({
        title: "Updated!",
        description: "Lettermeme has been updated successfully.",
      })
      navigate(settingsPath({}))
    },
  })
  const { loading: updateLoading } = mutationResult

  const onSubmit = (values: LettermemeFormValues) => {
    updateLettermeme({
      variables: {
        input: {
          id: id!,
          lettermemeInput: {
            name: values.name,
            urlPath: values.urlPath,
            shareableEmailIn: values.shareableEmailIn,
            publicDescription: values.publicDescription,
            emailHeaderText: values.emailHeaderText,
            summaryTonePrompt: values.summaryTonePrompt,
            cartoonStylePrompt: values.cartoonStylePrompt,
            emailCadence: values.emailCadence,
            updateCadence: values.updateCadence,
            userEmails: values.userEmails,
            privacy: values.privacy,
            generateCartoons: values.generateCartoons === "true",
          },
        },
      },
    })
  }

  if (error) return <GraphqlError error={error} />
  if (loading) return <ScreenLoading />

  if (!lettermeme) {
    return (
      <div className="mx-auto max-w-3xl p-8">
        <h1 className="mb-4 text-2xl font-bold">Lettermeme not found</h1>
        <p>The lettermeme you're looking for doesn't exist.</p>
      </div>
    )
  }

  const ownerEmails = lettermeme.lettermemeUsers.filter((lu) => lu.owner).map((lu) => lu.user.email)

  const nonOwnerEmails = lettermeme.lettermemeUsers
    .filter((lu) => !lu.owner)
    .map((lu) => lu.user.email)

  return (
    <div className="mx-auto mt-10 max-w-3xl p-8">
      <div className="mb-8 flex justify-between gap-4 lg:flex-row">
        <div>
          <Link to={myLettermemeManagePath({ id: lettermeme.id })} className="text-gray-400">
            ← back to lettermeme settings
          </Link>
          <h1 className="mt-4 text-2xl font-bold">Edit Lettermeme</h1>
          <p className="text-gray-400">Update your lettermeme settings</p>
        </div>
      </div>

      <LettermemeForm
        defaultValues={{
          name: lettermeme.name || "",
          urlPath: lettermeme.urlPath || "",
          shareableEmailIn: lettermeme.shareableEmailIn || "",
          secretEmailIn: `${lettermeme.secretEmailIn || ""}@lettermeme.com`,
          publicDescription: lettermeme.publicDescription || "",
          emailHeaderText: lettermeme.emailHeaderText || "",
          summaryTonePrompt: lettermeme.summaryTonePrompt || "",
          cartoonStylePrompt: lettermeme.cartoonStylePrompt || "",
          emailCadence: lettermeme.emailCadence,
          updateCadence: lettermeme.updateCadence,
          userEmails: nonOwnerEmails,
          privacy: lettermeme.privacy,
          generateCartoons: lettermeme.generateCartoons ? "true" : "false",
        }}
        ownerEmails={ownerEmails}
        onSubmit={onSubmit}
        isSubmitting={updateLoading}
        onCancel={() => navigate(settingsPath({}))}
        mutationResult={mutationResult}
      />
    </div>
  )
}
